import { useEffect, useState } from 'react';
import clsx from 'clsx';
import { Armchair, Bike, Clock, ShoppingBag } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Order, OrderStatus, OrderType, ORDER_STATUS_LABEL, ORDER_TYPE_LABEL } from '@/services/orders.service';

export const TYPE_ICON: Record<OrderType, LucideIcon> = { dine_in: Armchair, takeaway: ShoppingBag, delivery: Bike };

export const STATUS_TONE: Record<OrderStatus, { dot: string; pill: string }> = {
  new: { dot: 'bg-info', pill: 'bg-info-soft text-info' },
  preparing: { dot: 'bg-warning', pill: 'bg-warning-soft text-warning' },
  ready: { dot: 'bg-success', pill: 'bg-success-soft text-success' },
  out_for_delivery: { dot: 'bg-primary', pill: 'bg-primary-soft text-primary' },
  completed: { dot: 'bg-text-muted', pill: 'bg-surface-alt text-text-secondary' },
  cancelled: { dot: 'bg-danger', pill: 'bg-danger-soft text-danger' },
};

/** Label for the one-tap "move it along" button. */
export const NEXT_LABEL: Record<Exclude<OrderStatus, 'cancelled' | 'new'>, string> = {
  preparing: 'Start preparing',
  ready: 'Mark ready',
  out_for_delivery: 'Send out',
  completed: 'Complete',
};

/** A clock that re-renders its owner every `ms` (elapsed timers on the board). */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const h = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(h);
  }, [ms]);
  return now;
}

export function minutesSince(iso: string | null, now: number): number {
  if (!iso) return 0;
  return Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
}

export function elapsedLabel(mins: number): string {
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m`;
  const h = Math.floor(mins / 60);
  return h >= 24 ? `${Math.floor(h / 24)}d` : `${h}h ${String(mins % 60).padStart(2, '0')}m`;
}

/** Amber after 20 minutes, red after 40 — only while the order is still open. */
export function ElapsedPill({ order, now }: { order: Pick<Order, 'placedAt' | 'status' | 'completedAt'>; now: number }) {
  const open = order.status !== 'completed' && order.status !== 'cancelled';
  const mins = minutesSince(order.placedAt, open ? now : new Date(order.completedAt ?? order.placedAt).getTime());
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-2xs font-semibold tabular-nums',
        !open ? 'bg-surface-alt text-text-muted'
          : mins >= 40 ? 'bg-danger-soft text-danger'
            : mins >= 20 ? 'bg-warning-soft text-warning'
              : 'bg-surface-alt text-text-secondary',
      )}
      title={open ? `Placed ${mins} min ago` : `Took ${mins} min`}
    >
      <Clock size={10} /> {elapsedLabel(mins)}
    </span>
  );
}

export function StatusBadge({ status }: { status: OrderStatus }) {
  return (
    <span className={clsx('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold', STATUS_TONE[status].pill)}>
      <span className={clsx('h-1.5 w-1.5 rounded-full', STATUS_TONE[status].dot)} />
      {ORDER_STATUS_LABEL[status]}
    </span>
  );
}

export function TypeBadge({ order, compact }: { order: Pick<Order, 'type' | 'tableLabel'>; compact?: boolean }) {
  const Icon = TYPE_ICON[order.type];
  const text = order.type === 'dine_in' && order.tableLabel ? `Table ${order.tableLabel}` : ORDER_TYPE_LABEL[order.type];
  return (
    <span className="inline-flex min-w-0 items-center gap-1 rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold text-text-secondary" title={ORDER_TYPE_LABEL[order.type]}>
      <Icon size={11} className="shrink-0" />
      {!compact && <span className="truncate">{text}</span>}
    </span>
  );
}

export function PaymentBadge({ order }: { order: Pick<Order, 'paymentStatus' | 'paymentMethod'> }) {
  if (order.paymentStatus === 'paid') {
    return <span className="rounded-full bg-success-soft px-2 py-0.5 text-2xs font-semibold text-success">Paid{order.paymentMethod ? ` · ${order.paymentMethod}` : ''}</span>;
  }
  if (order.paymentStatus === 'refunded') return <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs font-semibold text-text-muted">Refunded</span>;
  return <span className="rounded-full bg-warning-soft px-2 py-0.5 text-2xs font-semibold text-warning">Unpaid</span>;
}

export const itemsSummary = (o: Pick<Order, 'items'>) => o.items.map((l) => `${l.quantity}× ${l.name}`).join(', ');

export const timeOf = (iso: string | null) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';

export const dateTimeOf = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';
